import {
  type Outlet,
  type OutletInput,
  type OutletWithCounts,
  SOURCE_CLASS_HELP,
  SOURCE_CLASS_LABELS,
  SOURCE_CLASSES,
  type SourceClass,
  type SourceRegistryOverview,
  type UnregisteredPublisher,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Newspaper, Pencil, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { EditorError, Panel, SelectRow, TextRow } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';

// Admin → Outlets: the source registry. Each outlet has an owner, a type and an independence group, so that two
// copies of one story or two outlets of one owner count as one source. Sources are matched to an outlet by their
// web domain or publisher name; the list at the bottom shows what sources cite that no outlet covers yet.

export const Route = createFileRoute('/admin/outlets')({ component: OutletsPage });

const KEY = ['admin', 'outlets'];

interface FormState {
  name: string;
  ownerName: string;
  sourceClass: SourceClass | '';
  independenceGroup: string;
  domains: string;
  aliases: string;
  notes: string;
}

const blank = (seed?: Partial<FormState>): FormState => ({
  name: '',
  ownerName: '',
  sourceClass: '',
  independenceGroup: '',
  domains: '',
  aliases: '',
  notes: '',
  ...seed,
});

const fromOutlet = (o: Outlet): FormState => ({
  name: o.name,
  ownerName: o.ownerName ?? '',
  sourceClass: o.sourceClass ?? '',
  independenceGroup: o.independenceGroup ?? '',
  domains: o.domains.join(', '),
  aliases: o.aliases.join(', '),
  notes: o.notes ?? '',
});

const list = (s: string) =>
  s
    .split(/[,\n]/)
    .map(x => x.trim())
    .filter(Boolean);

const toInput = (f: FormState): OutletInput => ({
  name: f.name,
  ownerName: f.ownerName || null,
  sourceClass: f.sourceClass || null,
  independenceGroup: f.independenceGroup || null,
  domains: list(f.domains),
  aliases: list(f.aliases),
  notes: f.notes || null,
});

const CLASS_OPTIONS = [{ value: '' as const, label: 'Not set' }, ...SOURCE_CLASSES.map(c => ({ value: c, label: SOURCE_CLASS_LABELS[c] }))];

function OutletsPage() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<SourceRegistryOverview>('/admin/outlets') });
  const [editing, setEditing] = useState<{ id: string | null; form: FormState } | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };

  const save = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: OutletInput }) =>
      withReauth(() =>
        id ? api<Outlet>(`/admin/outlets/${id}`, { method: 'PATCH', body: input }) : api<Outlet>('/admin/outlets', { body: input }),
      ),
    onSuccess: o => {
      setMessage({ kind: 'ok', text: `Saved “${o.name}”. Sources are matched to it from now on.` });
      setEditing(null);
      refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (o: Outlet) => withReauth(() => api(`/admin/outlets/${o.id}`, { method: 'DELETE' })),
    onSuccess: (_r, o) => {
      setMessage({ kind: 'ok', text: `Deleted “${o.name}”. Its sources no longer belong to an outlet.` });
      refresh();
    },
    onError,
  });
  const askDelete = async (o: OutletWithCounts) => {
    setMessage(null);
    const ok = confirm
      ? await confirm({
          title: `Delete “${o.name}”?`,
          body: 'Documents and cited sources set to it go back to being matched by domain or name. Claims count their sources again without it.',
          confirmLabel: 'Delete outlet',
          cancelLabel: 'Keep it',
          destructive: true,
        })
      : true;
    if (ok) remove.mutate(o);
  };
  const addFrom = (u: UnregisteredPublisher) => {
    setMessage(null);
    setEditing({ id: null, form: blank(u.kind === 'domain' ? { name: u.value, domains: u.value } : { name: u.value }) });
  };

  const data = q.data;
  return (
    <>
      <PageHeader
        title="Outlets"
        description="Who stands behind each source: its outlet, owner, type and independence group."
        actions={
          <Button
            size="sm"
            onClick={() => {
              setMessage(null);
              setEditing({ id: null, form: blank() });
            }}
          >
            <Plus aria-hidden="true" /> Add outlet
          </Button>
        }
      />
      <Tip id="admin-outlets" learnMore="source-registry">
        Two copies of one story, or two outlets of one owner, count as one source. Give outlets of the same owner the same owner, or the
        same independence group.
      </Tip>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={`mt-3 rounded-md border px-3 py-2 text-sm ${message.kind === 'error' ? 'border-danger/40 text-danger-foreground' : 'border-success/40'}`}
        >
          {message.text}
        </p>
      )}
      {editing && (
        <div className="mt-4">
          <OutletForm
            key={editing.id ?? 'new'}
            initial={editing.form}
            isNew={!editing.id}
            pending={save.isPending}
            error={save.error instanceof ApiError ? save.error.message : null}
            onCancel={() => setEditing(null)}
            onSave={f => save.mutate({ id: editing.id, input: toInput(f) })}
          />
        </div>
      )}
      <div className="mt-4 space-y-6">
        {q.isError ? (
          <ErrorState error={q.error} title="The outlets couldn’t be loaded" onRetry={() => q.refetch()} learnMore="source-registry" />
        ) : !data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <Panel title="Outlets">
              {data.outlets.length === 0 ? (
                <EmptyState icon={Newspaper} title="No outlets yet" learnMore="source-registry">
                  Add the papers, sites, institutions and people your sources come from, or start from the publishers listed below.
                </EmptyState>
              ) : (
                <div className="relative overflow-x-auto rounded-lg border bg-surface">
                  <table className="w-full text-sm" aria-label="Outlets">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="p-2 text-start font-medium">Outlet</th>
                        <th className="p-2 text-start font-medium">Owner</th>
                        <th className="p-2 text-start font-medium">Type</th>
                        <th className="p-2 text-start font-medium">Counts as</th>
                        <th className="p-2 text-start font-medium">Domains</th>
                        <th className="p-2 text-end font-medium">Sources</th>
                        <th className="p-2 text-start font-medium">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.outlets.map(o => (
                        <tr key={o.id} className="border-b last:border-0 align-top">
                          <td className="p-2">
                            <DataText className="font-medium">{o.name}</DataText>
                            {o.aliases.length > 0 && <div className="text-xs text-muted-foreground">also {o.aliases.join(', ')}</div>}
                          </td>
                          <td className="p-2">{o.ownerName ? <DataText>{o.ownerName}</DataText> : '—'}</td>
                          <td className="p-2">{o.sourceClass ? SOURCE_CLASS_LABELS[o.sourceClass] : 'Not set'}</td>
                          <td className="p-2">
                            <DataText>{o.group}</DataText>
                          </td>
                          <td className="p-2 text-xs">{o.domains.length ? o.domains.join(', ') : '—'}</td>
                          <td className="p-2 text-end">
                            <DataNumber>{o.documents + o.citedSources}</DataNumber>
                            <div className="text-xs text-muted-foreground">
                              {o.documents} {o.documents === 1 ? 'document' : 'documents'} · {o.citedSources} cited
                            </div>
                          </td>
                          <td className="p-2">
                            <span className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setMessage(null);
                                  setEditing({ id: o.id, form: fromOutlet(o) });
                                }}
                              >
                                <Pencil aria-hidden="true" /> Edit<span className="sr-only"> {o.name}</span>
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => askDelete(o)}>
                                <Trash2 aria-hidden="true" /> Delete<span className="sr-only"> {o.name}</span>
                              </Button>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Without an outlet: <DataNumber>{data.withoutOutlet.documents}</DataNumber> documents and{' '}
                <DataNumber>{data.withoutOutlet.citedSources}</DataNumber> cited sources. Each of them counts as its own source (or as its
                publisher, when it names one).
              </p>
            </Panel>
            <Panel title="Cited but not in the registry">
              {data.unregistered.length === 0 ? (
                <p className="text-sm text-muted-foreground">Every publisher and web domain your sources name belongs to an outlet.</p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Publishers and web domains your sources name, most cited first. Add one as an outlet, or add it to an existing outlet’s
                    other names or domains.
                  </p>
                  <ul className="divide-y rounded-md border" aria-label="Cited but not in the registry">
                    {data.unregistered.map(u => (
                      <li key={`${u.kind}:${u.value}`} className="flex flex-wrap items-center gap-2 p-2 text-sm">
                        <DataText className="font-medium">{u.value}</DataText>
                        <span className="text-xs text-muted-foreground">
                          {u.kind === 'domain' ? 'web domain' : 'publisher'} · {u.sources} {u.sources === 1 ? 'source' : 'sources'}
                        </span>
                        <Button size="sm" variant="outline" className="ms-auto" onClick={() => addFrom(u)}>
                          <Plus aria-hidden="true" /> Add as outlet<span className="sr-only"> {u.value}</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Panel>
          </>
        )}
        <LearnMore slug="source-registry" />
      </div>
    </>
  );
}

function OutletForm({
  initial,
  isNew,
  pending,
  error,
  onSave,
  onCancel,
}: {
  initial: FormState;
  isNew: boolean;
  pending: boolean;
  error: string | null;
  onSave: (f: FormState) => void;
  onCancel: () => void;
}) {
  const [f, setF] = useState(initial);
  const set = (p: Partial<FormState>) => setF(prev => ({ ...prev, ...p }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave(f);
  };
  return (
    <form
      className="space-y-3 rounded-lg border bg-surface p-3"
      aria-label={isNew ? 'New outlet' : `Edit ${initial.name}`}
      onSubmit={submit}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <TextRow label="Name" required value={f.name} onChange={v => set({ name: v })} placeholder="e.g. Le Monde" />
        <TextRow
          label="Owner"
          value={f.ownerName}
          onChange={v => set({ ownerName: v })}
          placeholder="e.g. Groupe Le Monde"
          help="Outlets with the same owner count as one source."
        />
        <SelectRow<SourceClass | ''>
          label="Type"
          value={f.sourceClass}
          options={CLASS_OPTIONS}
          onChange={v => set({ sourceClass: v })}
          help={SOURCE_CLASSES.map(c => `${SOURCE_CLASS_LABELS[c]}: ${SOURCE_CLASS_HELP[c]}`).join(' ')}
          learnMore="source-registry"
        />
        <TextRow
          label="Independence group"
          value={f.independenceGroup}
          onChange={v => set({ independenceGroup: v })}
          placeholder="Empty: the owner, else the outlet"
          help="Outlets in the same group count as one source, e.g. papers that print the same wire story."
        />
        <TextRow
          label="Web domains"
          value={f.domains}
          onChange={v => set({ domains: v })}
          placeholder="lemonde.fr, lemonde.com"
          help="Comma-separated. Pages on these domains and their subdomains belong to this outlet."
        />
        <TextRow
          label="Other names"
          value={f.aliases}
          onChange={v => set({ aliases: v })}
          placeholder="LeMonde.fr, Le Monde (Paris)"
          help="Comma-separated. Cited sources whose publisher has one of these names belong to this outlet."
        />
      </div>
      <TextRow label="Notes" multiline value={f.notes} onChange={v => set({ notes: v })} />
      <EditorError error={error} />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!f.name.trim() || pending}>
          {isNew ? 'Add outlet' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
